/**
 * API Configuration utility
 * Provides environment-aware API URL configuration for the application
 */

// Determine the environment
const isProduction = process.env.REACT_APP_ENV === 'production';

// Get the appropriate base URL based on environment
export const API_BASE_URL = isProduction 
  ? process.env.REACT_APP_PROD_API_URL 
  : process.env.REACT_APP_DEV_API_URL || 'http://localhost:3000';

// API endpoint for base path
export const API_PATH = '/api/v1/e-prorab';

// Complete API URL with path
export const API_URL = `${API_BASE_URL}${API_PATH}`;

/**
 * Constructs an image URL based on the provided path
 * Handles both relative and absolute paths
 * @param {string|object} imagePath - The image path to process
 * @returns {string} The full image URL
 */
export const getImageUrl = (imagePath) => {
  // Handle empty values
  if (!imagePath) return '';
  
  // Log the imagePath for debugging
  console.log('getImageUrl input:', imagePath);
  
  // Handle objects by extracting the path property
  if (typeof imagePath === 'object') {
    // Extract path from object
    if (imagePath.path) {
      // Get direct path
      console.log('Using direct path from object:', imagePath.path);
      imagePath = imagePath.path;
    } 
    // If no path, try url
    else if (imagePath.url) {
      console.log('Using URL from object:', imagePath.url);
      imagePath = imagePath.url;
    }
    // If no path or url, try ID with mono-files as last resort
    else if (imagePath._id || imagePath.id) {
      const imageId = imagePath._id || imagePath.id;
      console.log('Falling back to mono-files with ID:', imageId);
      return `${getApiEndpoint('mono-files/download')}/${imageId}?nocache=${Date.now()}`;
    }
    else {
      console.error('Cannot extract path from object:', imagePath);
      return '';
    }
  }
  
  // Final check to ensure imagePath is a string
  if (typeof imagePath !== 'string') {
    console.error('getImageUrl: Non-string value after processing:', imagePath);
    try {
      imagePath = String(imagePath);
    } catch (e) {
      console.error('getImageUrl: Failed to convert to string:', e);
      return '';
    }
  }
  
  // Now proceed with string operations
  
  // If the path already starts with http(s), it's an absolute URL
  if (imagePath.startsWith('http')) {
    console.log('Using absolute URL:', imagePath);
    return imagePath;
  }
  
  // DIRECT UPLOADS ACCESS: Bypass mono-files endpoint and access uploads directly
  if (imagePath.includes('uploads/')) {
    // Extract the filename 
    const parts = imagePath.split('/');
    const filename = parts[parts.length - 1];
    
    // Construct the full URL to the uploads directory
    const uploadsUrl = `https://api.eprorab.com/uploads/${filename}`;
    console.log('Created direct uploads URL:', uploadsUrl);
    
    // Add cache busting
    return `${uploadsUrl}?t=${Date.now()}`;
  }
  
  // Legacy fallback
  // If the path already includes our API path, don't add it again
  if (imagePath.includes(API_PATH)) {
    return `${API_BASE_URL}${imagePath}`;
  }
  
  // If the path starts with a slash, remove it to avoid double slashes
  const normalizedPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
  
  // Add a timestamp to prevent caching
  const timestamp = Date.now();
  const finalUrl = `${API_BASE_URL}/${normalizedPath}`;
  return `${finalUrl}${finalUrl.includes('?') ? '&' : '?'}t=${timestamp}`;
};

const getApiEndpoint = (path = '') => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  const baseURL = isDevelopment
    ? 'https://api.eprorab.com/api/v1/e-prorab'
    : 'https://api.eprorab.com/api/v1/e-prorab';

  // If path is empty, just return the base URL
  if (!path) return baseURL;
  
  // Normalize the path by removing leading slash if present
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  
  // Combine base URL with normalized path
  return `${baseURL}/${normalizedPath}`;
};

export { getApiEndpoint };

// For debugging purposes
console.log('API Configuration:');
console.log('Environment:', isProduction ? 'Production' : 'Development');
console.log('API Base URL:', API_BASE_URL);
console.log('API URL:', API_URL);

// Test a sample endpoint to verify configuration is working
console.log('Sample API endpoint:', getApiEndpoint('repairs/sample-id'));

// Named export object
const apiConfig = {
  API_BASE_URL,
  API_PATH,
  API_URL,
  getImageUrl,
  getApiEndpoint,
  isProduction
};

export default apiConfig; 